import { useEffect, useState } from 'react';
import { OPEN_ACCOUNT_EVENT } from '../app-open.js';
import { api } from '../api.js';
import { getDisplayUnits } from '../units.js';
import { readSalinityUnit } from '../salinity.js';
import { probeName, probeReadingText, pickRows, missingPicks } from '../probe-pick.js';
import { useConfirm } from './ConfirmDialog.jsx';
import { unreadLine, orpCardOffer } from '../hydros-inputs.js';

// Per-tank HYDROS controller selection, shown in the Equipment section.
// Account linking (one CoralVue login per user) lives in the account
// menu (HydrosAccountPanel) — this panel ONLY assigns one of that
// account's controllers to THIS tank. A controller drives exactly one
// tank, so the picker hides controllers already assigned elsewhere.

export function HydrosLinkPanel({ tank, onReload }) {
  // A tank shared with this keeper is its owner's to link. The connect /
  // link / unlink controls would act on the viewer's own HYDROS account or
  // be refused, so they see the owner's link read-only.
  if (tank.role && tank.role !== 'owner') return <SharedHydrosStatus tank={tank} />;
  return <OwnerHydrosLinkPanel tank={tank} onReload={onReload} />;
}

function OwnerHydrosLinkPanel({ tank, onReload }) {
  const confirm = useConfirm();
  const [account, setAccount] = useState(null); // null = loading
  const [link, setLink] = useState(null); // GET /tanks/:id/hydros payload
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');

  const reloadStatus = async () => {
    try {
      const acct = await api('/api/hydros');
      setAccount(acct);
      if (tank.hydros_thing_id) {
        setLink(await api(`/api/tanks/${tank.id}/hydros`));
      } else {
        setLink({ linked: false });
      }
    } catch (e) {
      setAccount({ connected: false, unavailable: true });
      setLink({ linked: false });
      setErr(e.status === 503 ? '' : e.message || 'Could not load HYDROS status.');
    }
  };

  useEffect(() => {
    reloadStatus();
  }, [tank.id, tank.hydros_thing_id]);

  // ORP is kept as history (feedback #72); its card is the keeper's to add.
  const orpOffer = link && !link.stale ? orpCardOffer(tank, link.values) : null;
  const addOrpCard = async () => {
    setBusy('orp');
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/params`, { method: 'PATCH', body: { params: orpOffer } });
      await onReload();
      setNote('ORP is on Home now, as history: no range, no score and no alerts.');
    } catch (e) {
      setErr(e.message || 'Could not add the ORP card.');
    } finally {
      setBusy(null);
    }
  };

  const linkController = async () => {
    if (!pick) {
      setErr('Pick a controller.');
      return;
    }
    setBusy('link');
    setErr('');
    try {
      const out = await api(`/api/tanks/${tank.id}/hydros`, {
        method: 'PUT',
        body: { thing_id: pick },
      });
      setNote(
        out?.shared_with?.length
          ? `This controller also feeds ${out.shared_with.join(', ')}, which keeps reading its probes. Pick the probes that sit in ${tank.name} below.`
          : ''
      );
      setPick('');
      await onReload();
      await reloadStatus();
    } catch (e) {
      setErr(e.payload?.detail || e.message || 'Could not link the controller.');
    } finally {
      setBusy(null);
    }
  };

  const unlinkController = async () => {
    const ok = await confirm({
      title: `Unlink this controller from ${tank.name}?`,
      body: 'Live HYDROS readings will stop landing in this tank until you re-link a controller. The controller stays on your HYDROS account either way.',
      confirmLabel: 'Unlink',
      destructive: true,
    });
    if (!ok) return;
    setBusy('unlink');
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/hydros`, { method: 'DELETE' });
      await onReload();
      await reloadStatus();
    } catch (e) {
      setErr(e.message || 'Could not unlink.');
    } finally {
      setBusy(null);
    }
  };

  if (account === null) {
    return (
      <div className="ts-add-form" style={{ marginBottom: 14 }}>
        <p className="ts-hint">Checking HYDROS…</p>
      </div>
    );
  }

  const controllers = account.controllers || [];
  const viaKey = controllers.find((c) => c.thing_id === tank.hydros_thing_id)?.source === 'api';
  // A password-linked controller drives exactly one tank: offer only those
  // unassigned or on THIS tank. A key-connected one (a whole Collective)
  // can feed several tanks, each reading its own probes.
  const available = controllers.filter(
    (c) => c.source === 'api' || !c.assigned_tank_id || c.assigned_tank_id === tank.id
  );
  const linkedName =
    link?.controller?.name ||
    controllers.find((c) => c.thing_id === tank.hydros_thing_id)?.name ||
    tank.hydros_thing_id;

  return (
    <div className="ts-add-form" style={{ marginBottom: 14 }}>
      <div className="ts-add-head">
        <h4 className="font-heading">HYDROS controller</h4>
      </div>

      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}

      {tank.hydros_thing_id ? (
        <>
          <div className="ts-row-body" style={{ marginTop: 8, alignItems: 'center' }}>
            <strong>{linkedName}</strong>
            {link?.stale ? (
              <span className="ts-hint">no data yet</span>
            ) : (
              <span className="ts-hint">
                {link?.controller?.online ? 'Online' : 'Offline'}
                {link?.controller?.last_seen_s != null &&
                  ` · seen ${formatAgo(link.controller.last_seen_s)} ago`}
              </span>
            )}
          </div>
          {!link?.stale && (link?.values || []).length > 0 && (
            <p className="ts-hint" style={{ marginTop: 4 }}>
              {probeValuesLine(link.values, tank)}
            </p>
          )}
          {viaKey && link && !link.stale && unreadLine(link.unread_series) && (
            <p className="ts-hint" style={{ marginTop: 6 }}>
              {unreadLine(link.unread_series)}
            </p>
          )}
          {orpOffer && (
            <div className="hk-orp-offer">
              <button
                type="button"
                className="hk-btn"
                onClick={addOrpCard}
                disabled={busy === 'orp'}
              >
                {busy === 'orp' ? 'Adding…' : 'Show ORP on Home'}
              </button>
              <span className="ts-hint">
                History only: TankSensei has no ORP range, so it is not scored or alerted on. To see
                HIGH and LOW on its card, set your own range in Parameters. It still sends no
                alerts.
              </span>
            </div>
          )}
          {viaKey && link && !link.stale && link.can_edit && (
            <ProbeChoice tank={tank} link={link} onSaved={reloadStatus} />
          )}
          {viaKey && link && !link.stale && link.can_edit && (
            <HistoryImport key={tank.hydros_thing_id} tank={tank} onDone={setNote} />
          )}
          {note && (
            <p className="ts-hint" role="status" style={{ marginTop: 6 }}>
              {note}
            </p>
          )}
          {viaKey ? (
            <p className="ts-hint" style={{ marginTop: 6 }}>
              Connected with a HYDROS key. TankSensei reads the controller's probe log every few
              minutes and keeps a reading every 15 minutes at most, so your Home cards and score
              follow it. Temperature, pH, salinity and alkalinity readings fill the tank's cards for
              them, count in the score and can alert. ORP is kept as history, with no score or
              alerts. Outlets, water level and leak sensors are not read through a key yet. Calcium,
              magnesium and manual inputs in HYDROS are not read either: log those here as readings.
              A key made on one controller reads only that controller's probes; for a salinity probe
              on another controller in a Collective, make the key on the Collective.
            </p>
          ) : (
            <>
              <p className="ts-hint" style={{ marginTop: 6 }}>
                Sensei logs these probe values as readings a few times a day, so your Home cards and
                score follow the controller automatically.
              </p>
              <p className="ts-hint" style={{ marginTop: 6 }}>
                This tank reads only the probes on this controller. In a HYDROS Collective each
                controller carries its own probes, so if salinity or another probe is missing, it is
                wired to a different controller. A tank reads one controller here, so to get probes
                from several, connect with a HYDROS key made on the Collective, which reads every
                controller's probes. Temperature, pH, salinity and alkalinity become parameter
                cards, count in the score and can alert. ORP is kept as history, with no score or
                alerts. Water level, leak sensors and outlets show on the HYDROS card on Home only.
                Calcium, magnesium and manual inputs in HYDROS are not read: log those here as
                readings.
              </p>
            </>
          )}
          <div className="ts-row-body" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="hk-btn"
              onClick={unlinkController}
              disabled={busy === 'unlink'}
            >
              {busy === 'unlink' ? '…' : 'Unlink from this tank'}
            </button>
          </div>
        </>
      ) : !account.connected ? (
        <>
          <p className="ts-hint" style={{ marginTop: 8 }}>
            Connect HYDROS in Account first, then come back here to pick the controller for this
            tank.
          </p>
          <OpenConnectHydros label="Connect HYDROS" />
        </>
      ) : available.length === 0 ? (
        controllers.length === 0 ? (
          <p className="ts-hint" style={{ marginTop: 8 }}>
            No controllers found on this HYDROS account yet. If you just added one, give it a minute
            and reopen this sheet.
          </p>
        ) : (
          <>
            <p className="ts-hint" style={{ marginTop: 8 }}>
              Each HYDROS controller can drive only one tank, and yours are already taken:
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0' }}>
              {controllers.map((c) => (
                <li key={c.thing_id} style={{ padding: '4px 0', fontSize: 15 }}>
                  <strong>{c.name}</strong>{' '}
                  <span className="ts-hint">
                    → {c.assigned_tank_name || 'another tank'}
                    {c.online ? '' : ' (offline)'} · {probeList(c.probes)}
                  </span>
                </li>
              ))}
            </ul>
            <p className="ts-hint" style={{ marginTop: 6 }}>
              Open that tank's Equipment and devices, its HYDROS controller, and tap "Unlink" to
              move a controller here.
            </p>
          </>
        )
      ) : (
        <div className="ts-row-body" style={{ marginTop: 10 }}>
          <label className="ts-field" style={{ minWidth: 200, flex: '1 1 200px' }}>
            <span>Controller for {tank.name}</span>
            <select value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Pick…</option>
              {available.map((c) => (
                <option key={c.thing_id} value={c.thing_id}>
                  {c.name}
                  {c.online ? '' : ' (offline)'} · {probeList(c.probes)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="ts-save"
            onClick={linkController}
            disabled={busy === 'link'}
          >
            {busy === 'link' ? '…' : 'Link'}
          </button>
          <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', width: '100%' }}>
            {available.map((c) => (
              <li key={c.thing_id} className="ts-hint" style={{ padding: '2px 0' }}>
                <strong>{c.name}</strong>: {probeList(c.probes)}
              </li>
            ))}
          </ul>
          <p className="ts-hint" style={{ marginTop: 4 }}>
            A tank reads only the controller it is linked to. Pick the one your probes are wired to.
          </p>
        </div>
      )}
      {account.connected && account.api?.available && (
        <OpenConnectHydros label="Add a HYDROS key in Account" quiet />
      )}
    </div>
  );
}

// Opens Account > Connect HYDROS over this page, no reload (AppShell
// listens for the event).
function OpenConnectHydros({ label, quiet = false }) {
  return (
    <div className="ts-row-body" style={{ marginTop: 10 }}>
      <button
        type="button"
        className={quiet ? 'hk-btn' : 'ts-save'}
        onClick={() =>
          window.dispatchEvent(new CustomEvent(OPEN_ACCOUNT_EVENT, { detail: 'hydros' }))
        }
      >
        {label}
      </button>
    </div>
  );
}

// The probe values in the keeper's own units (°C on a Celsius tank, S.G.
// for a keeper who reads S.G.): "Temperature 25.9 °C · pH 8.12".
function probeValuesLine(values, tank) {
  const opts = { tempUnit: getDisplayUnits(tank).temp, salinityUnit: readSalinityUnit() };
  return values.map((v) => `${probeName(v.key)} ${probeReadingText(v, opts)}`).join(' · ');
}

// The read-only view for a keeper the tank is shared with: what the owner
// linked and whether it is reporting, with no controls.
function SharedHydrosStatus({ tank }) {
  const [link, setLink] = useState(() => (tank.hydros_thing_id ? null : { linked: false }));
  useEffect(() => {
    let alive = true;
    if (!tank.hydros_thing_id) {
      setLink({ linked: false });
      return undefined;
    }
    api(`/api/tanks/${tank.id}/hydros`)
      .then((r) => alive && setLink(r))
      .catch(() => alive && setLink({ linked: true, stale: true }));
    return () => {
      alive = false;
    };
  }, [tank.id, tank.hydros_thing_id]);

  return (
    <div className="ts-add-form" style={{ marginBottom: 14 }}>
      <div className="ts-add-head">
        <h4 className="font-heading">HYDROS controller</h4>
      </div>
      {link === null ? (
        <p className="ts-hint" style={{ marginTop: 8 }}>
          Checking HYDROS…
        </p>
      ) : !link.linked ? (
        <p className="ts-hint" style={{ marginTop: 8 }}>
          No HYDROS controller is linked to {tank.name}. Only the tank's owner can link one.
        </p>
      ) : (
        <>
          <div className="ts-row-body" style={{ marginTop: 8, alignItems: 'center' }}>
            <strong>{link.controller?.name || 'HYDROS controller'}</strong>
            <span className="ts-hint">
              {link.stale
                ? 'no data yet'
                : `${link.controller?.online ? 'Online' : 'Offline'}${
                    link.controller?.last_seen_s != null
                      ? ` · seen ${formatAgo(link.controller.last_seen_s)} ago`
                      : ''
                  }`}
            </span>
          </div>
          {!link.stale && (link.values || []).length > 0 && (
            <p className="ts-hint" style={{ marginTop: 4 }}>
              {probeValuesLine(link.values, tank)}
            </p>
          )}
          <p className="ts-hint" style={{ marginTop: 6 }}>
            The tank's owner linked this controller. Only they can change or unlink it.
          </p>
        </>
      )}
    </div>
  );
}

const PARAM_NAME = {
  temp: 'Temperature',
  ph: 'pH',
  salinity: 'Salinity',
  alk: 'Alkalinity',
  orp: 'ORP',
};

// A key covers a whole Collective, which can carry the display's and the
// frag tank's temperature probes. Where a controller has more than one
// probe of a kind, or feeds more than one tank, the keeper says which
// probe is in THIS tank; until then that parameter is not read here.
export function ProbeChoice({ tank, link, onSaved }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const series = link.series || {};
  const shared = (link.shared_with || []).length > 0;
  const params = pickRows(link);
  if (!params.length) return null;
  const choice = link.choice || {};
  const needs = new Set(link.needs_choice || []);
  const missing = new Set(missingPicks(link));

  const save = async (param, value) => {
    setBusy(param);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/hydros/series`, {
        method: 'PUT',
        body: { choice: { [param]: value === '__none' ? null : value } },
      });
      await onSaved();
    } catch (e) {
      setErr(e.payload?.error || e.message || 'Could not save.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="hk-block">
      <div className="hk-title">Which probes are in {tank.name}?</div>
      <p className="ts-hint" style={{ marginTop: 4 }}>
        {shared
          ? `This controller also feeds ${link.shared_with.join(', ')}. Pick the probes that sit in this tank. A parameter is not read here until you pick.`
          : params.every((p) => missing.has(p) && (series[p] || []).length < 2)
            ? 'Not read here until the picked probe sends readings again, or you pick another.'
            : 'This controller has more than one probe of a kind. Pick the one in this tank. A parameter is not read here until you pick.'}
      </p>
      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}
      {params.map((p) => {
        const value =
          choice[p] === null ? '__none' : typeof choice[p] === 'string' ? choice[p] : '';
        return (
          <div key={p}>
            <label className="hk-pick-row">
              <span className="hk-pick-name">
                {PARAM_NAME[p]}
                {needs.has(p) && <span className="hk-pick-need">Not read yet</span>}
              </span>
              <select
                className="hk-pick-select"
                value={value}
                disabled={busy === p}
                onChange={(e) => e.target.value && save(p, e.target.value)}
              >
                <option value="">Pick a probe…</option>
                {missing.has(p) && (
                  <option value={choice[p]} disabled>
                    {choice[p]} (no readings lately)
                  </option>
                )}
                {(series[p] || []).map((name) => (
                  <option key={name} value={name}>
                    {name}
                    {link.series_values?.[name]
                      ? ` · ${probeReadingText(
                          {
                            key: link.series_values[name].param,
                            value: link.series_values[name].value,
                          },
                          {
                            tempUnit: getDisplayUnits(tank).temp,
                            salinityUnit: readSalinityUnit(),
                          }
                        )}`
                      : ''}
                  </option>
                ))}
                <option value="__none">Not in this tank</option>
              </select>
            </label>
            {missing.has(p) && (
              <p className="ts-hint" style={{ margin: '4px 0 0' }}>
                &ldquo;{choice[p]}&rdquo;, the probe picked here, has sent no readings lately. Check
                it, or pick again if it was renamed in HYDROS.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// The import is remembered on this device, per tank and controller, so the
// button does not come back on the next visit but does for a controller
// linked later. Another phone may offer it again; a second run skips every
// reading already there, so that is harmless.
const historyKey = (tankId, thingId) => `aqs.hydrosHistory.${tankId}.${thingId}`;

export function readHistoryImportedAt(tankId, thingId) {
  try {
    const v = globalThis.localStorage?.getItem(historyKey(tankId, thingId));
    return v && Number.isFinite(new Date(v).getTime()) ? v : null;
  } catch {
    return null;
  }
}

export function rememberHistoryImported(tankId, thingId, at = new Date()) {
  try {
    globalThis.localStorage?.setItem(historyKey(tankId, thingId), at.toISOString());
  } catch {
    // Private mode or blocked storage: the button simply shows again.
  }
}

export function historyImportedLine(iso) {
  const d = new Date(iso);
  const day = d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  return `History brought in on ${day}.`;
}

// The controller's older log into this tank, only when the keeper says the
// controller was on this tank the whole time: a controller moved from
// another tank would bring that tank's water with it.
function HistoryImport({ tank, onDone }) {
  const [busy, setBusy] = useState(false);
  const [importedAt, setImportedAt] = useState(() =>
    readHistoryImportedAt(tank.id, tank.hydros_thing_id)
  );
  const [err, setErr] = useState('');
  if (importedAt) {
    return (
      <div className="hk-block">
        <div className="hk-title">Earlier readings</div>
        <p className="ts-hint" style={{ marginTop: 4 }}>
          {historyImportedLine(importedAt)}
        </p>
      </div>
    );
  }
  const run = async () => {
    setBusy(true);
    setErr('');
    try {
      const out = await api(`/api/tanks/${tank.id}/hydros/history`, { method: 'POST' });
      // No probes picked or no live key: nothing was brought in, keep the button.
      if (!out?.reason || out.reason === 'too_new') {
        const at = new Date();
        rememberHistoryImported(tank.id, tank.hydros_thing_id, at);
        setImportedAt(at.toISOString());
      }
      onDone(
        out?.written
          ? `Added ${out.written} older readings from HYDROS.`
          : out?.reason === 'no_probes'
            ? "Pick this tank's probes first, then bring the history in."
            : 'HYDROS had no older readings to add for this tank.'
      );
    } catch (e) {
      setErr(e.payload?.detail || e.message || 'Could not bring the history in.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="hk-block">
      <div className="hk-title">Earlier readings</div>
      <p className="ts-hint" style={{ marginTop: 4 }}>
        If this controller has been on {tank.name} for the last month, TankSensei can bring in its
        readings from up to 30 days back (never from before you added the tank), one every two
        hours. Skip this if the controller was on another tank.
      </p>
      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}
      <button type="button" className="hk-btn" onClick={run} disabled={busy}>
        {busy ? 'Bringing them in…' : 'Bring in the last 30 days'}
      </button>
    </div>
  );
}

const PROBE_LABEL = {
  temp: 'temperature',
  ph: 'pH',
  salinity: 'salinity',
  alk: 'alkalinity',
  orp: 'ORP',
  level: 'water level',
  leak: 'leak',
};

// "temperature, pH, salinity" for the picker; the keeper picks the
// controller their probes are wired to (feedback #57: a Collective's
// salinity probe was on another controller than the one linked).
function probeList(probes) {
  if (!Array.isArray(probes)) return 'no readings received yet';
  if (!probes.length) return 'no probes reporting';
  return probes.map((k) => PROBE_LABEL[k] || k).join(', ');
}

function formatAgo(seconds) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}
